import { Info, Phone, Sparkle, UserIcon, Users } from "@/components/icons";
import {
  Badge,
  Button,
  Input,
  useDebounceCallback,
} from "@/components/kit";
import {
  FieldContent,
  FieldLabel,
  Field as FieldLayout,
} from "@/components/ui/field";
import { cn } from "@/lib/utils";
import type { CustomerDTO } from "@/modules/customers/domain/types";
import { useCustomerQuery } from "@/modules/customers/presentation/api/queries";
import type { CustomerInputDTO } from "@/modules/orders/domain/contracts";
import { useCustomerSearch } from "@/modules/orders/presentation/api/queries";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

export type CustomerIdentityValue = {
  name: string;
  phone: string;
  customerId?: string;
  forceNewCustomer: boolean;
};

export function customerIdentityToInput(
  value: CustomerIdentityValue,
): CustomerInputDTO {
  return {
    ...(value.customerId && !value.forceNewCustomer
      ? { id: value.customerId }
      : {}),
    name: value.name.trim(),
    phone: value.phone.trim() || null,
  };
}

type CustomerIdentityFieldsProps = {
  value: CustomerIdentityValue;
  onChange: (value: CustomerIdentityValue) => void;
  idPrefix?: string;
  nameRequired?: boolean;
};

export function CustomerIdentityFields({
  value,
  onChange,
  idPrefix = "cust",
  nameRequired = true,
}: CustomerIdentityFieldsProps) {
  const [customerQuery, setCustomerQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const debouncedCustomer = useDebounceCallback(
    (query: string) => setCustomerQuery(query),
    350,
  );

  const customerResults = useCustomerSearch(customerQuery);
  const selectedCustomer = useCustomerQuery(value.customerId ?? "");

  const suggestions = customerResults.data?.data ?? [];
  const isLinked = Boolean(value.customerId && !value.forceNewCustomer);
  const isForceNew = value.forceNewCustomer;
  const hasTypedName = value.name.trim().length > 0;
  const canSearch = customerQuery.trim().length >= 2;

  const panelMode = useMemo(() => {
    if (isLinked) return "linked" as const;
    if (isForceNew) return "force-new" as const;
    if (canSearch && suggestions.length > 0 && hasTypedName) {
      return "ambiguous" as const;
    }
    if (hasTypedName) return "new" as const;
    return null;
  }, [
    isLinked,
    isForceNew,
    canSearch,
    suggestions.length,
    hasTypedName,
  ]);

  function updateSearchQuery(nextName: string, nextPhone: string) {
    debouncedCustomer([nextName.trim(), nextPhone.trim()].filter(Boolean).join(" "));
  }

  function applyCustomer(customer: CustomerDTO) {
    onChange({
      name: customer.name,
      phone: customer.phone ?? "",
      customerId: customer.id,
      forceNewCustomer: false,
    });
    setShowSuggestions(false);
    setCustomerQuery("");
  }

  function handleForceNewCustomer() {
    onChange({
      ...value,
      customerId: undefined,
      forceNewCustomer: true,
    });
    setShowSuggestions(false);
  }

  function handleNameChange(nextName: string) {
    onChange({
      ...value,
      name: nextName,
      customerId: undefined,
      forceNewCustomer: false,
    });
    updateSearchQuery(nextName, value.phone);
    setShowSuggestions(true);
  }

  function handlePhoneChange(nextPhone: string) {
    onChange({
      ...value,
      phone: nextPhone,
      customerId: undefined,
      forceNewCustomer: false,
    });
    updateSearchQuery(value.name, nextPhone);
    setShowSuggestions(true);
  }

  const showSuggestionDropdown =
    showSuggestions &&
    canSearch &&
    !isLinked &&
    !isForceNew &&
    suggestions.length > 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <FieldLayout className="relative">
          <FieldLabel htmlFor={`${idPrefix}-name`}>
            ຊື່{nameRequired ? " *" : ""}
          </FieldLabel>
          <FieldContent>
            <Input
              id={`${idPrefix}-name`}
              className="rounded-xl"
              value={value.name}
              onChange={(e) => handleNameChange(e.target.value)}
              onFocus={() => value.name.trim() && setShowSuggestions(true)}
              placeholder="ຊື່ລູກຄ້າ"
              autoComplete="off"
            />
          </FieldContent>
        </FieldLayout>

        <FieldLayout>
          <FieldLabel htmlFor={`${idPrefix}-phone`}>
            <Phone className="size-4" /> ເບີໂທ
          </FieldLabel>
          <FieldContent>
            <Input
              id={`${idPrefix}-phone`}
              className="rounded-xl"
              value={value.phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              placeholder="020..."
              inputMode="tel"
              autoComplete="off"
            />
          </FieldContent>
        </FieldLayout>
      </div>

      {showSuggestionDropdown ? (
        <div className="rounded-xl border border-secondary/20 bg-card p-1">
          <p className="px-3 py-1.5 text-muted-foreground text-xs">
            ລູກຄ້າເກົ່າ — ກົດເພື່ອເຊື່ອມກັບບັນທຶກ
          </p>
          {suggestions.map((customer) => (
            <button
              key={customer.id}
              type="button"
              onClick={() => applyCustomer(customer)}
              className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-secondary/10"
            >
              <span className="font-medium">{customer.name}</span>
              <span className="text-muted-foreground text-xs">
                {customer.phone ?? "—"}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {panelMode ? (
        <CustomerIdentityStatusPanel
          mode={panelMode}
          customer={selectedCustomer.data}
          suggestions={suggestions}
          onSelectCustomer={applyCustomer}
          onForceNewCustomer={handleForceNewCustomer}
        />
      ) : null}
    </div>
  );
}

function CustomerIdentityStatusPanel({
  mode,
  customer,
  suggestions,
  onSelectCustomer,
  onForceNewCustomer,
}: {
  mode: "linked" | "force-new" | "ambiguous" | "new";
  customer?: {
    id: string;
    name: string;
    phone: string | null;
    stats: { orderCount: number; isReturning: boolean; lastOrderAt: Date | null };
  };
  suggestions: CustomerDTO[];
  onSelectCustomer: (customer: CustomerDTO) => void;
  onForceNewCustomer: () => void;
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border px-4 py-3",
        mode === "linked" &&
          "border-leaf/30 bg-leaf/5",
        mode === "force-new" &&
          "border-secondary/30 bg-secondary/8",
        mode === "ambiguous" &&
          "border-primary/25 bg-primary/5",
        mode === "new" &&
          "border-border/60 bg-muted/25",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div
            className={cn(
              "flex size-10 shrink-0 items-center justify-center rounded-2xl",
              mode === "linked" && "bg-leaf/15 text-leaf",
              mode === "force-new" && "bg-secondary/15 text-secondary",
              mode === "ambiguous" && "bg-primary/15 text-primary",
              mode === "new" && "bg-muted text-muted-foreground",
            )}
          >
            {mode === "linked" ? (
              <Users className="size-4" />
            ) : mode === "force-new" || mode === "new" ? (
              <Sparkle className="size-4" />
            ) : (
              <Info className="size-4" />
            )}
          </div>

          <div className="min-w-0">
            {mode === "linked" && customer ? (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium text-sm">ລູກຄ້າເກົ່າ</p>
                  {customer.stats.isReturning ? (
                    <Badge className="rounded-full border-leaf/30 bg-leaf/10 text-leaf">
                      ຊື້ຊ້ຳ · {customer.stats.orderCount} ອໍເດີ້
                    </Badge>
                  ) : customer.stats.orderCount > 0 ? (
                    <Badge variant="secondary" className="rounded-full">
                      {customer.stats.orderCount} ອໍເດີ້
                    </Badge>
                  ) : null}
                </div>
                <p className="mt-1 text-muted-foreground text-sm">
                  {customer.name}
                  {customer.phone ? ` · ${customer.phone}` : ""}
                </p>
                <Link
                  to="/app/customers/$id"
                  params={{ id: customer.id }}
                  className="mt-1 inline-flex text-secondary text-xs hover:underline"
                >
                  ເບິ່ງໂປຣໄຟລ໌ລູກຄ້າ
                </Link>
              </>
            ) : null}

            {mode === "force-new" ? (
              <>
                <p className="font-medium text-sm">ລູກຄ້າໃໝ່ (ບັງຄັບ)</p>
                <p className="mt-1 text-muted-foreground text-sm">
                  ຈະສ້າງບັນທຶກລູກຄ້າໃໝ່ເມື່ອບັນທຶກ ແມ້ຊື່ຈະຄ້າຍຄືກກັບລູກຄ້າເກົ່າ
                </p>
              </>
            ) : null}

            {mode === "ambiguous" ? (
              <>
                <p className="font-medium text-sm">ພົບລູກຄ້າເກົ່າທີ່ຄ້າຍຄືກກັນ</p>
                <p className="mt-1 text-muted-foreground text-sm">
                  ເລືອກລູກຄ້າເກົ່າ ຫຼື ຕັ້ງເປັນລູກຄ້າໃໝ່ຖ້າເປັນຄົນອື່ນ
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {suggestions.slice(0, 4).map((customer) => (
                    <Button
                      key={customer.id}
                      type="button"
                      size="sm"
                      variant="outline"
                      className="rounded-full border-secondary/30"
                      onClick={() => onSelectCustomer(customer)}
                    >
                      <UserIcon className="size-3.5" />
                      {customer.name}
                    </Button>
                  ))}
                </div>
              </>
            ) : null}

            {mode === "new" ? (
              <>
                <p className="font-medium text-sm">ລູກຄ້າໃໝ່</p>
                <p className="mt-1 text-muted-foreground text-sm">
                  ບໍ່ພົບຊື່ໃນລະບົບ — ຈະສ້າງບັນທຶກລູກຄ້າໃໝ່ເມື່ອບັນທຶກ
                </p>
              </>
            ) : null}
          </div>
        </div>

        {mode === "linked" || mode === "ambiguous" ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="shrink-0 rounded-full border-secondary/30"
            onClick={onForceNewCustomer}
          >
            <Sparkle data-icon="inline-start" />
            ຕັ້ງເປັນລູກຄ້າໃໝ່
          </Button>
        ) : null}
      </div>
    </div>
  );
}
